/**
 * injectTenantCss
 *
 * Stylesheet loading strategy: URL-first, bundled tenant.module.css as fallback.
 *
 * Step 1 — URL (tenant.css):
 *   a. If a <link rel="stylesheet" href="*tenant.css"> already exists in the
 *      document (declared in index.html), it is moved to the END of <head>.
 *      This ensures it is positioned after all Vite-injected <style> tags so
 *      the tenant CSS variables win the cascade over the bundled defaults.
 *      Moving the existing node re-uses the browser's cached response — no
 *      extra network request is made.
 *   b. If no such link exists but VITE_CDN_URL is configured, a new <link>
 *      is appended to <head> pointing to ${VITE_CDN_URL}/tenant.css.
 *
 * Step 2 — Fallback:
 *   If neither (a) nor (b) provides a URL, the bundled tenant.module.css
 *   defaults remain active as the silent fallback.
 *
 * Step 3 — Asset fallbacks positioning:
 *   The #asset-fallbacks <style> injected by injectAssetVars() is moved to
 *   end of <head> just before the tenant.css link. This ensures the cascade
 *   order is:
 *
 *     Vite-bundled styles (tenant.module.css, etc.)
 *       ↓  loses to
 *     #asset-fallbacks   ← BASE_URL-prefixed local paths (correct fallback)
 *       ↓  loses to
 *     tenant.css         ← CDN values win when defined
 *
 *   Without this repositioning, tenant.module.css (injected by Vite after
 *   JS execution starts) would win over #asset-fallbacks and supply image
 *   paths without the Vite BASE_URL prefix, causing 404s on sub-path deploys.
 *
 * Step 4 — Image URL resolution (CORS-free, Strategy A):
 *   resolveImageVarsFromCascade() runs after the tenant.css link's styles are
 *   applied.  It reads every --image-* variable from getComputedStyle (no
 *   fetch / no CORS dependency), resolves any relative url() path to an
 *   absolute URL using the tenant.css href as the base, and applies the
 *   resolved value as an inline style.  This fixes relative url() references
 *   like url("asset/banner.png") that would otherwise resolve to the document
 *   origin rather than the CDN origin when used via var() in emotion styles.
 *
 * Step 5 — Font injection (best-effort, Strategy B):
 *   parseTenantCssAssets() fetches the CSS text in the background to extract
 *   @font-face blocks and re-inject them with absolute src URLs.  Silently
 *   no-ops on CORS/network failure.
 */
import { getCdnUrl } from "./asset";
import { parseTenantCssAssets, resolveImageVarsFromCascade } from "./parseTenantCssAssets";

/** Move the #asset-fallbacks <style> to end of <head> so it sits between
 *  Vite-bundled styles and tenant.css in the cascade. */
function repositionAssetFallbacks(): void {
  const fallbacks = document.getElementById("asset-fallbacks");
  if (fallbacks) document.head.appendChild(fallbacks);
}

/**
 * Run callback once the stylesheet's rules are applied to the cascade.
 * For a link that was already loaded (link.sheet !== null) we wait one
 * animation frame to ensure the browser has recalculated computed styles
 * after any DOM reordering done earlier in this tick.
 */
function whenStylesApplied(link: HTMLLinkElement, callback: () => void): void {
  if (link.sheet) {
    // Stylesheet already loaded — defer one frame so getComputedStyle
    // reflects the final cascade after our head reordering.
    requestAnimationFrame(callback);
  } else {
    // Wait for the network load then defer one frame.
    link.addEventListener(
      "load",
      () => requestAnimationFrame(callback),
      { once: true }
    );
  }
}

export function injectTenantCss(): void {
  if (typeof document === "undefined") return;

  // Step 1a: re-position any tenant.css <link> already in the DOM so it
  // lands after Vite's <style> injections and wins the CSS cascade.
  const existingLink = document.querySelector<HTMLLinkElement>(
    'link[rel="stylesheet"][href*="tenant.css"]'
  );
  if (existingLink) {
    // Step 3: move #asset-fallbacks BEFORE the tenant.css link
    repositionAssetFallbacks();
    document.head.appendChild(existingLink); // moves node, no re-fetch

    const href = existingLink.href;

    // Step 4: resolve relative image URLs once cascade is ready (CORS-free)
    whenStylesApplied(existingLink, () => resolveImageVarsFromCascade(href));

    // Step 5: best-effort @font-face injection via fetched CSS text
    parseTenantCssAssets(href);
    return;
  }

  // Step 1b: no static link found — try VITE_CDN_URL.
  const cdnHref = getCdnUrl("tenant.css");
  if (!cdnHref) {
    // Step 2: no URL source — reposition #asset-fallbacks so it still wins
    // over tenant.module.css with the correct BASE_URL paths.
    repositionAssetFallbacks();
    return;
  }

  const link = document.createElement("link");
  link.rel  = "stylesheet";
  link.href = cdnHref;

  // Step 3: move #asset-fallbacks before the new tenant.css link
  repositionAssetFallbacks();

  document.head.appendChild(link);

  // Step 4: resolve relative image URLs once the new link has loaded
  whenStylesApplied(link, () => resolveImageVarsFromCascade(cdnHref));

  // Step 5: best-effort @font-face injection via fetched CSS text
  parseTenantCssAssets(cdnHref);
}
